import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './UpcomingSection.css';
import TournamentCard from './tournaments/TournamentCard';
import GameCard from './games/GameCard';
import { API_URL } from '../config';


// Cache für API-Aufrufe (5 Minuten)
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 Minuten

// Hilfsfunktion für gecachte API-Aufrufe
const cachedFetch = async (url, cacheKey) => {
  const now = Date.now();
  const cached = cache.get(cacheKey);
  
  if (cached && (now - cached.timestamp < CACHE_DURATION)) {
    return cached.data;
  }
  
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    
    const data = await response.json();
    cache.set(cacheKey, { data, timestamp: now });
    return data;
  } catch (error) {
    // Return cached data if available, even if expired
    if (cached) {
      console.warn(`Using expired cache for ${cacheKey}:`, error);
      return cached.data;
    }
    throw error;
  }
};

// Optimierte Spiele-Bewertung Laden (batch API calls)
const loadGamesWithRatings = async (games) => {
  const batchSize = 5; // Reduzierte batch size für bessere Performance
  const results = [];
  
  for (let i = 0; i < games.length; i += batchSize) {
    const batch = games.slice(i, i + batchSize);
    const batchPromises = batch.map(async (game) => {
      const gameKey = game.key || game.name;
      if (!gameKey) return { ...game, averageRating: 0, ratingCount: 0 };
      
      try {
        const ratings = await cachedFetch(
          `${API_URL}/api/game-ratings/${encodeURIComponent(gameKey)}`,
          `rating-${gameKey}`
        );
        
        return {
          ...game,
          averageRating: ratings.average || 0,
          ratingCount: ratings.count || 0
        };
      } catch (error) {
        console.warn(`Failed to load ratings for ${game.name}:`, error);
        return { ...game, averageRating: 0, ratingCount: 0 };
      }
    });
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
    
    // Kleine Pause zwischen Batches für bessere Performance
    if (i + batchSize < games.length) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }
  
  return results;
};

function UpcomingSection() {
  const [tournaments, setTournaments] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Hook für Screen Size Detection
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1200); // Mobile/Tablet = < 1200px
    };
    
    checkScreenSize(); // Initial check
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Anzahl der Items basierend auf Screen Size
  const getItemsLimit = () => {
    const width = window.innerWidth;
    // Turniere: Immer 3
    // Spiele: 3 auf Mobil, 6 auf Desktop/Tablet
    if (width < 768) {
      return { tournaments: 3, games: 3 }; // Mobil: 3 Turniere, 3 Spiele
    }
    return { tournaments: 3, games: 6 }; // Desktop/Tablet: 3 Turniere, 6 Spiele
  };
  
  const itemsLimit = getItemsLimit();
  const gamesLimit = itemsLimit.games;
  const tournamentsLimit = itemsLimit.tournaments;

  // Memoized sortierte und gefilterte Turniere
  const sortedTournaments = useMemo(() => {
    return tournaments
      .filter(t => {
        const status = (t.status || '').toLowerCase();
        // Zeige alle anstehenden Turniere unabhängig vom Datum
        return ['coming_soon', 'upcoming', 'scheduled'].includes(status);
      })
      .sort((a, b) => {
        // Hilfsfunktion um zu prüfen ob ein Datum gültig ist
        const hasValidDate = (tournament) => {
          const dateStr = tournament.startAt || tournament.startDate;
          if (!dateStr || dateStr === 'TBA' || dateStr === 'tba') return false;
          
          const date = new Date(dateStr);
          return !isNaN(date.getTime()) && date.getFullYear() > 2000;
        };
        
        const aHasDate = hasValidDate(a);
        const bHasDate = hasValidDate(b);
        
        // 1. Turniere mit Datum haben immer Priorität
        if (aHasDate && !bHasDate) return -1;
        if (!aHasDate && bHasDate) return 1;
        
        // 2. Beide haben Datum: chronologisch sortieren
        if (aHasDate && bHasDate) {
          const dateA = new Date(a.startAt || a.startDate);
          const dateB = new Date(b.startAt || b.startDate);
          
          if (dateA.getTime() !== dateB.getTime()) {
            return dateA - dateB;
          }
          // Bei gleichem Datum: alphabetisch
          return (a.title || '').localeCompare(b.title || '');
        }
        
        // 3. Beide haben kein Datum: alphabetisch sortieren
        return (a.title || '').localeCompare(b.title || '');
      })
      .slice(0, tournamentsLimit);
  }, [tournaments, tournamentsLimit]);

  // Debug: Log sorted tournaments
  console.log('🏆 Sorted Tournaments:', sortedTournaments.map(t => ({
    title: t.title,
    startAt: t.startAt,
    startDate: t.startDate,
    hasValidDate: (() => {
      const dateStr = t.startAt || t.startDate;
      if (!dateStr || dateStr === 'TBA' || dateStr === 'tba') return false;
      const date = new Date(dateStr);
      return !isNaN(date.getTime()) && date.getFullYear() > 2000;
    })(),
    status: t.status
  })));

  // Memoized sortierte und gefilterte Spiele
  const sortedGames = useMemo(() => {
    const gamesWithMinRatings = games.filter(g => g.ratingCount >= 10);
    const gamesWithoutMinRatings = games.filter(g => g.ratingCount < 10);

    let selectedGames = [];

    if (gamesWithMinRatings.length > 0) {
      selectedGames = gamesWithMinRatings
        .sort((a, b) => b.averageRating - a.averageRating)
        .slice(0, gamesLimit);
    }

    if (selectedGames.length < gamesLimit) {
      const needed = gamesLimit - selectedGames.length;
      const additionalGames = gamesWithoutMinRatings
        .sort((a, b) => {
          if (b.averageRating !== a.averageRating) {
            return b.averageRating - a.averageRating;
          }
          return (a.name || '').localeCompare(b.name || '');
        })
        .slice(0, needed);
        
      selectedGames = [...selectedGames, ...additionalGames];
    }

    return selectedGames.slice(0, gamesLimit);
  }, [games, gamesLimit]);

  // Debug: Log sorted games
  console.log(`🎮 Games (${isMobile ? 'Mobile' : 'Desktop'} - Limit: ${gamesLimit}):`, sortedGames.map(g => ({
    title: g.name,
    rating: g.averageRating,
    count: g.ratingCount
  })));

  // ✅ State für Full Games Data (für TournamentCard)
  const [allGamesData, setAllGamesData] = useState([]);

  // Optimierte Load-Funktion mit useCallback
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Parallele API-Aufrufe mit Caching
      const [tournamentData, gamesData] = await Promise.all([
        cachedFetch(
          `${API_URL}/api/tournaments?sort=date&limit=20`,
          'tournaments-all'
        ).catch(() => ({ items: [] })),
        cachedFetch(
          `${API_URL}/api/games?limit=30`,
          'games-list'
        ).catch(() => [])
      ]);

      // ✅ Store FULL games data für TournamentCard
      if (Array.isArray(gamesData) && gamesData.length > 0) {
        setAllGamesData(gamesData);
      }

      // Turniere setzen
      const tournamentList = tournamentData.items || tournamentData || [];
      setTournaments(tournamentList);

      // Spiele mit Bewertungen laden (optimiert)
      if (Array.isArray(gamesData) && gamesData.length > 0) {
        // Nur die ersten 12 Spiele für bessere Performance
        const limitedGames = gamesData.slice(0, 12);
        const gamesWithRatings = await loadGamesWithRatings(limitedGames);
        setGames(gamesWithRatings);
      } else {
        console.warn('Games response is not an array or empty:', gamesData);
        setGames([]);
      }
      
    } catch (error) {
      console.error('Error loading upcoming section data:', error);
      setError('Failed to load data. Please try again later.');
      setTournaments([]);
      setGames([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Loading Skeleton Komponente
  const LoadingSkeleton = ({ count = 4 }) => (
    <div className="loading-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="loading-card">
          <div className="loading-image"></div>
          <div className="loading-content">
            <div className="loading-line"></div>
            <div className="loading-line short"></div>
          </div>
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <section className="upcoming-section">
        <div className="upcoming-container">
          <h2 className="upcoming-title">Discover What's Next</h2>
          <p className="upcoming-subtitle">Upcoming tournaments and top-rated games</p>
          
          <div className="upcoming-content loading">
            <div className="upcoming-block">
              <h3 className="block-title">Upcoming Tournaments</h3>
              <LoadingSkeleton count={3} />
            </div>
            
            <div className="upcoming-block">
              <h3 className="block-title">Popular Games</h3>
              <LoadingSkeleton count={gamesLimit} />
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="upcoming-section">
        <div className="upcoming-container">
          <h2 className="upcoming-title">Discover What's Next</h2>
          <p className="upcoming-subtitle">Upcoming tournaments and top-rated games</p>
          
          <div className="upcoming-error">
            <h3>⚠️ Loading Error</h3>
            <p>{error}</p>
            <button 
              className="va-btn primary" 
              onClick={() => {
                cache.clear(); // Clear cache on retry
                loadData();
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="upcoming-section">
      <div className="upcoming-container">
        <h2 className="upcoming-title">Discover What's Next</h2>
        <p className="upcoming-subtitle">Upcoming tournaments and top-rated games</p>
        
        <div className="upcoming-content">
          {/* Tournaments Block */}
          <div className="upcoming-block">
            <div className="block-header">
              <h3 className="block-title">Upcoming Tournaments</h3>
              <Link to="/tournaments" className="block-link">
                View All →
              </Link>
            </div>
            
            {sortedTournaments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🏆</div>
                <h4>No Upcoming Tournaments</h4>
                <p>New tournaments will be available soon!</p>
                <Link to="/tournaments" className="va-btn ghost">
                  Browse Tournaments
                </Link>
              </div>
            ) : (
              <div className="tournaments-grid">
                {sortedTournaments.map((tournament, index) => (
                  <TournamentCard 
                    key={tournament._id || tournament.id || index}
                    data={tournament}
                    gamesData={allGamesData}
                    compact={true}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Games Block */}
          <div className="upcoming-block">
            <div className="block-header">
              <h3 className="block-title">Popular Games</h3>
              <Link to="/games" className="block-link">
                View All →
              </Link>
            </div>
            
            {sortedGames.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🎮</div>
                <h4>No Games Available</h4>
                <p>Games are loading...</p>
                <Link to="/games" className="va-btn ghost">
                  Browse Games
                </Link>
              </div>
            ) : (
              <div className="games-grid">
                {sortedGames.map((game, index) => (
                  <GameCard 
                    key={game._id || game.key || game.name || index}
                    game={game}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default UpcomingSection;